import { FormEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import apiClient from "../../api-services/ApiClient";
import { API } from "../../api-services/ApiList";
import { SITELINKS } from "../../app-routes/links";
import { forgotPasswordSchema } from "../../interface/auth.interface";
import { getErrorMessage } from "../../utils";
import AuthLayout from "./component/AuthLayout";
import {
  AuthHeading,
  BackLink,
  FormAlert,
  SubmitButton,
  TextField,
  textLinkClass,
} from "./component/FormControls";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = (location.state as { email?: string } | null)?.email ?? "";

  const [email, setEmail] = useState(prefill);
  const [error, setError] = useState("");
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setServerError("");

    const result = forgotPasswordSchema.safeParse({ email });
    if (!result.success) {
      setError(result.error.errors[0]?.message ?? "Enter a valid email address");
      return;
    }

    setError("");
    setLoading(true);
    try {
      await apiClient.post(API.FORGOT_PASSWORD, result.data);
      navigate(SITELINKS.RESET_PASSWORD, {
        state: {
          email: result.data.email,
          justSent: true,
          notice: `We've sent a 6-digit reset code to ${result.data.email}. It can take a minute to arrive.`,
        },
      });
    } catch (err) {
      setServerError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Reset password | HoneConnect Admin">
      <BackLink to={SITELINKS.SIGNIN} label="Back to sign in" />

      <AuthHeading
        title="Reset your password"
        description="Enter the email address on your admin account and we'll send you a code to set a new password."
      />

      <FormAlert tone="error" message={serverError} />

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <TextField
          id="email"
          name="email"
          type="email"
          label="Email address"
          placeholder="name@company.com"
          autoComplete="username"
          inputMode="email"
          autoFocus={!prefill}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError("");
          }}
          error={error}
        />

        <div className="pt-3">
          <SubmitButton loading={loading} loadingText="Sending code">
            Send reset code
          </SubmitButton>
        </div>
      </form>

      <p className="mt-8 text-center text-sm text-honeConnectGray">
        Already have a code?{" "}
        <Link
          to={SITELINKS.RESET_PASSWORD}
          state={{ email: email.trim() }}
          className={textLinkClass}
        >
          Enter it here
        </Link>
      </p>
    </AuthLayout>
  );
};

export default ForgotPassword;
